import { FC } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Autocomplete, TextField, TextFieldProps, Typography } from '@mui/material';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import { DebouncedState } from 'use-debounce';
import { isEmpty } from 'lodash';

type Props = {
  name: string;
  label: string;
  companies: string[];
  debouncedCompaniesRefetch: DebouncedState<(name: string) => void>;
  scope?: 'company' | 'office';
};

const RHFCompanySelect: FC<Props & TextFieldProps> = ({
  name,
  label,
  companies,
  debouncedCompaniesRefetch,
  scope,
  ...other
}) => {
  const { control, setValue, getValues } = useFormContext();
  const companyType = !!scope ? scope : 'company';

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          autoHighlight
          selectOnFocus
          clearOnEscape
          options={companies}
          disabled={other.disabled}
          freeSolo
          value={getValues(field.name) || ''}
          defaultValue={getValues(field.name)}
          onChange={(event, newValue) => {
            field.value = newValue || '';
            field.onChange(event);
            setValue(field.name, newValue || '');
            setValue(`${companyType}.strict`, !isEmpty(companies));
          }}
          onClose={(event, _reason) => {
            const option = event.currentTarget as HTMLElement;
            const checkValue = option.innerText.replace(/\s\s+/g, ' ');
            checkValue === field.value && setValue(`${companyType}.strict`, !isEmpty(companies));
          }}
          onInputChange={(event, data) => {
            debouncedCompaniesRefetch(data);
            !isEmpty(event) &&
              getValues(`${companyType}.strict`) &&
              setValue(`${companyType}.strict`, false);
          }}
          popupIcon={null}
          renderOption={(props, option, { inputValue }) => {
            const matches = match(option, inputValue);
            const parts = parse(option, matches);

            return (
              <li {...props}>
                {parts.map((part, index) => (
                  <>
                    {index !== 0 && parts[index - 1].text.slice(-1) === ' ' && (
                      <Typography component="pre" variant="subtitle2" key={index}>
                        {' '}
                      </Typography>
                    )}
                    <Typography
                      component="pre"
                      variant="subtitle2"
                      key={part.text}
                      fontWeight={part.highlight ? 700 : 400}
                    >
                      {part.text}
                    </Typography>
                    {index < parts.length - 1 && parts[index + 1].text.slice(0, 1) === ' ' && (
                      <Typography component="pre" variant="subtitle2" key={index + part.text}>
                        {' '}
                      </Typography>
                    )}
                  </>
                ))}
              </li>
            );
          }}
          renderInput={(params) => <TextField label={label} {...params} {...other} {...field} />}
        />
      )}
    />
  );
};

export default RHFCompanySelect;
